import { Alert, Typography } from '@mui/material';
import cn from 'classnames';
import { FormikProps } from 'formik';
import { useCallback } from 'react';
import * as React from 'react';
import { FileError, FileRejection } from 'react-dropzone';
import { useSelector } from 'react-redux';

import mskuBodyHintImage from '../../../assets/images/hints/msku-body-hint.png';
import mskuTitleHintImage from '../../../assets/images/hints/msku-title-hint.png';
import spBodyHintImage from '../../../assets/images/hints/shipping-page-body-hint.png';
import spButtonColorHintImage from '../../../assets/images/hints/shipping-page-button-color-hint.png';
import spHeaderColorHintImage from '../../../assets/images/hints/shipping-page-header-color-hint.png';
import spTitleHintImage from '../../../assets/images/hints/shipping-page-title-hint.png';

import {
  DELAYED_CUSTOMIZE_ITEMS_PAGE_BODY,
  DELAYED_CUSTOMIZE_ITEMS_PAGE_TITLE,
  DELAYED_MSKU_PAGE_BODY,
  DELAYED_MSKU_PAGE_TITLE,
  DELAYED_SHIPPING_BOX_IMAGE_URL,
  DELAYED_SHIPPING_BUTTON_COLOR,
  DELAYED_SHIPPING_COMPANY_NAME,
  DELAYED_SHIPPING_HEADER_COLOR,
  DELAYED_SHIPPING_LOGO_URL,
  DELAYED_SHIPPING_PAGE_BODY,
  DELAYED_SHIPPING_PAGE_TITLE,
} from '../../../constants/campaigns';
import useUploadFile from '../../../hooks/useUploadFile';
import useWindowSize from '../../../hooks/useWindowSize';
import { selectDelayedShippingTextCustomizationTags } from '../../../store/selectors/shell';
import { ICampaignCandidate, ICampaignSummary } from '../../../types/campaigns';
import { IDropzoneResult, ITextCustomizationForm } from '../../../types/forms';
import { PositionStylesEnum } from '../../../types/shell';
import { IUploadResponse, UploadFileTypeEnum } from '../../../types/upload';
import notification from '../../../utils/notification';
import { preparedDropZoneErrors } from '../../../utils/upload';
import { HtmlView, ImageUploadInput, TabProvider, TemplateTagPicker } from '../../index';

import { ITabProviderTab } from '../../TabProvider/TabProvider';
import { HelpTooltip } from '../../tooltips';
import { Input, TinyEditor } from '../inputs';
import ColorInput from '../inputs/ColorInput/ColorInput';
import { InputLabel } from '../labels';

import styles from './ShippingPageCustomizationForm.module.scss';

type TCustomizeFormFields = Pick<
  ICampaignCandidate,
  | 'ds_shipping_page_title'
  | 'ds_shipping_page_body'
  | 'ds_msku_page_title'
  | 'ds_msku_page_body'
  | 'ds_customize_items_page_title'
  | 'ds_customize_items_page_body'
>;

export interface IShippingPageCustomizationFormProps {
  readOnly?: boolean;
  isDisabled?: boolean;
  showTitle?: boolean;
  requiredFields: Record<string, boolean>;
  onChange?: (name: keyof ICampaignCandidate, value: string | number | boolean | undefined) => void;
  form: FormikProps<Partial<ICampaignCandidate>>;
  hasTemplateSelected?: boolean;
}

const INACTIVE_STYLE: React.CSSProperties = { opacity: 0.5, pointerEvents: 'none' };

const ShippingPageCustomizationForm: React.FC<IShippingPageCustomizationFormProps> = ({
  form,
  requiredFields,
  isDisabled,
  readOnly,
  onChange,
  showTitle,
  hasTemplateSelected,
}) => {
  const { mobile } = useWindowSize();

  const [customActiveTab, setCustomActiveTab] = React.useState<number>(0);

  const { uploadFile } = useUploadFile();

  const availableDSTags = useSelector(selectDelayedShippingTextCustomizationTags);

  const shouldHideButtonColorInput = React.useMemo(() => {
    return readOnly && !form.values[DELAYED_SHIPPING_BUTTON_COLOR];
  }, [form.values[DELAYED_SHIPPING_BUTTON_COLOR], readOnly]);

  const shouldHideHeaderColorInput = React.useMemo(() => {
    return readOnly && !form.values[DELAYED_SHIPPING_HEADER_COLOR];
  }, [form.values[DELAYED_SHIPPING_HEADER_COLOR], readOnly]);

  const shouldHideLogoImageInput = React.useMemo(() => {
    return readOnly && !form.values[DELAYED_SHIPPING_LOGO_URL];
  }, [form.values[DELAYED_SHIPPING_LOGO_URL], readOnly]);

  const shouldHideBoxImageInput = React.useMemo(() => {
    return readOnly && !form.values[DELAYED_SHIPPING_BOX_IMAGE_URL];
  }, [form.values[DELAYED_SHIPPING_BOX_IMAGE_URL], readOnly]);

  const shouldHideCompanyNameInput = React.useMemo(() => {
    return readOnly && !form.values[DELAYED_SHIPPING_COMPANY_NAME];
  }, [form.values[DELAYED_SHIPPING_COMPANY_NAME], readOnly]);

  const shouldHideForm = React.useMemo(() => {
    return [
      DELAYED_SHIPPING_BUTTON_COLOR,
      DELAYED_SHIPPING_HEADER_COLOR,
      DELAYED_SHIPPING_PAGE_TITLE,
      DELAYED_SHIPPING_PAGE_BODY,
      DELAYED_MSKU_PAGE_TITLE,
      DELAYED_MSKU_PAGE_BODY,
      DELAYED_CUSTOMIZE_ITEMS_PAGE_TITLE,
      DELAYED_CUSTOMIZE_ITEMS_PAGE_BODY,
      DELAYED_SHIPPING_LOGO_URL,
      DELAYED_SHIPPING_COMPANY_NAME,
      DELAYED_SHIPPING_BOX_IMAGE_URL,
    ].every((value) => readOnly && !form.values[value as keyof ICampaignSummary]);
  }, [form.values, readOnly]);

  const getFieldErrors = React.useCallback(
    (fieldName: string) => {
      const { touched, error } = form.getFieldMeta(fieldName);
      return touched ? error : undefined;
    },
    [form.getFieldMeta],
  );

  const handleFieldChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const { name, value } = e.currentTarget;

      if (typeof onChange === 'function') {
        onChange(name as keyof ICampaignCandidate, value);
      }
    },
    [onChange],
  );

  const handleColorChange = React.useCallback(
    (name: string, color: string) => {
      if (typeof onChange === 'function') {
        onChange(name as keyof ICampaignCandidate, color);
      }
    },
    [onChange],
  );

  const onUploadStart = React.useCallback(
    ({ files, resolve, reject }: IDropzoneResult) => {
      uploadFile(files[0], UploadFileTypeEnum.OrgLogo).then(resolve).catch(reject);
    },
    [uploadFile],
  );

  const onUploadImageSuccess = React.useCallback(
    (files: File[], response: IUploadResponse, fieldName: keyof ICampaignCandidate) => {
      const { public_url: url } = response;
      if (typeof onChange === 'function') {
        onChange(fieldName, url);
      }
    },
    [onChange],
  );

  const handleUploadLogoSuccess = useCallback(
    (files: File[], response: IUploadResponse) => onUploadImageSuccess(files, response, DELAYED_SHIPPING_LOGO_URL),
    [onUploadImageSuccess],
  );

  const handleUploadBoxSuccess = useCallback(
    (files: File[], response: IUploadResponse) => onUploadImageSuccess(files, response, DELAYED_SHIPPING_BOX_IMAGE_URL),
    [onUploadImageSuccess],
  );

  const onUploadFailure = React.useCallback((errors: FileError[]) => {
    const showError = ({ message }: any) => notification.error(message, { content: message });

    Array.isArray(errors) ? errors.map(showError) : showError(errors);
  }, []);

  const onDropReject = React.useCallback(
    (fileRejections: FileRejection[]) => {
      fileRejections.forEach((fileReject) => {
        const { errors } = fileReject;
        onUploadFailure(preparedDropZoneErrors(errors));
      });
    },
    [onUploadFailure],
  );

  const onImageDelete = React.useCallback(
    (fieldName: keyof ICampaignCandidate) => {
      if (typeof onChange === 'function') {
        onChange(fieldName, '');
      }
    },
    [onChange],
  );

  const customizeForms: ITextCustomizationForm[] = React.useMemo(
    () => [
      ...(readOnly && !form.values[DELAYED_SHIPPING_PAGE_TITLE] && !form.values[DELAYED_SHIPPING_PAGE_BODY]
        ? []
        : [
            {
              id: 'shipping-page',
              tabLabel: 'Claim page',
              title_name: DELAYED_SHIPPING_PAGE_TITLE,
              body_name: DELAYED_SHIPPING_PAGE_BODY,
              shouldHideTitle: readOnly && !form.values[DELAYED_SHIPPING_PAGE_TITLE],
              shouldHideBody: readOnly && !form.values[DELAYED_SHIPPING_PAGE_BODY],
              titleLabel: 'Claim page title',
              bodyLabel: 'Claim page body',
              titleHintImage: spTitleHintImage,
              bodyHintImage: spBodyHintImage,
            },
          ]),
      ...(readOnly && !form.values[DELAYED_MSKU_PAGE_TITLE] && !form.values[DELAYED_MSKU_PAGE_BODY]
        ? []
        : [
            {
              id: 'msku-page',
              tabLabel: 'MSKU page',
              title_name: DELAYED_MSKU_PAGE_TITLE,
              body_name: DELAYED_MSKU_PAGE_BODY,
              shouldHideTitle: readOnly && !form.values[DELAYED_MSKU_PAGE_TITLE],
              shouldHideBody: readOnly && !form.values[DELAYED_MSKU_PAGE_BODY],
              titleLabel: 'MSKU step title',
              bodyLabel: 'MSKU step body',
              titleHintImage: mskuTitleHintImage,
              bodyHintImage: mskuBodyHintImage,
            },
          ]),
    ],
    [form.values, readOnly],
  );

  const customizationItems: ITabProviderTab[] = React.useMemo(
    () =>
      customizeForms.map((item) => ({
        label: item.tabLabel,
        tabId: item.id,
        renderer: (
          <div className={styles.fields}>
            {!item.shouldHideTitle && (
              <div className={styles.info}>
                <div className={styles.inputHeadingContainer}>
                  <InputLabel
                    value={
                      <React.Fragment>
                        {item.titleLabel}
                        <HelpTooltip
                          id={`${item.id}-title-hint`}
                          className={styles.infoIcon}
                          place={mobile ? PositionStylesEnum.Right : PositionStylesEnum.Top}
                          contentClassName={styles.tooltipImage}
                        >
                          <img src={item.titleHintImage} alt="" />
                        </HelpTooltip>
                      </React.Fragment>
                    }
                  />
                </div>
                <Input
                  className={styles.inputContainer}
                  name={item.title_name}
                  field={form.getFieldProps(item.title_name)}
                  value={form.values[item.title_name as keyof TCustomizeFormFields]}
                  onChange={handleFieldChange}
                  disabled={isDisabled}
                  required={requiredFields[item.title_name]}
                  error={getFieldErrors(item.title_name)}
                  showErrors
                  readOnly={readOnly}
                />
              </div>
            )}
            {!item.shouldHideBody && (
              <div className={styles.info}>
                <div className={styles.inputHeadingContainer}>
                  <InputLabel
                    value={
                      <React.Fragment>
                        {item.bodyLabel}
                        <HelpTooltip
                          id={`${item.id}-body-hint`}
                          className={styles.infoIcon}
                          place={mobile ? PositionStylesEnum.Right : PositionStylesEnum.Top}
                          contentClassName={styles.tooltipImage}
                        >
                          <img src={item.bodyHintImage} alt="" />
                        </HelpTooltip>
                      </React.Fragment>
                    }
                  />
                </div>
                {readOnly ? (
                  <HtmlView
                    className={cn(styles.inputContainer, styles.htmlEditor, styles.viewContainer)}
                    html={form.values?.[item.body_name as keyof TCustomizeFormFields] || ''}
                  />
                ) : (
                  <TinyEditor
                    field={form.getFieldProps(item.body_name)}
                    value={form.values?.[item.body_name as keyof TCustomizeFormFields]}
                    changeCallback={handleFieldChange}
                    className={cn(styles.inputContainer, styles.htmlEditor)}
                    onBlur={form.handleBlur}
                    showHint={false}
                    disabled={isDisabled}
                    showFontColorPicker={false}
                  />
                )}
              </div>
            )}
          </div>
        ),
      })),
    [customizeForms, form, getFieldErrors, handleFieldChange, isDisabled, mobile, readOnly, requiredFields],
  );

  return shouldHideForm ? null : (
    <div className={styles.formWrapper}>
      {showTitle && (
        <h2 className={styles.heading}>
          <span>Claim page customization</span>
        </h2>
      )}
      <form className={styles.container}>
        {!readOnly && (
          <Typography fontWeight="medium" sx={{ fontSize: 18, mb: 1.5 }}>
            Claim Page settings
          </Typography>
        )}
        {!readOnly && (
          <Alert severity={hasTemplateSelected ? 'info' : 'warning'} sx={{ mb: '10px' }}>
            {hasTemplateSelected
              ? 'Selected template takes priority \u2014 the settings below are inactive'
              : 'Leave these inputs empty to use organization default values'}
          </Alert>
        )}
        <div style={hasTemplateSelected ? INACTIVE_STYLE : undefined}>
          <div className={styles.mainInfo}>
            <div className={styles.imageUploadWrappper}>
              {!shouldHideLogoImageInput && (
                <ImageUploadInput
                  message="Click or drag to upload logo"
                  tooltipTitle="Custom logo"
                  onUpload={onUploadStart}
                  onSuccess={handleUploadLogoSuccess}
                  onFailure={onUploadFailure}
                  onDropReject={onDropReject}
                  onBlur={() => form.getFieldHelpers(DELAYED_SHIPPING_LOGO_URL).setTouched(true, true)}
                  value={form.values?.[DELAYED_SHIPPING_LOGO_URL]}
                  onImageDelete={() => onImageDelete(DELAYED_SHIPPING_LOGO_URL)}
                  readOnly={readOnly}
                  error={getFieldErrors(DELAYED_SHIPPING_LOGO_URL)}
                  showErrors
                />
              )}
              {!shouldHideBoxImageInput && (
                <ImageUploadInput
                  message="Click or drag to upload box image"
                  tooltipTitle="Custom box image"
                  onUpload={onUploadStart}
                  onSuccess={handleUploadBoxSuccess}
                  onFailure={onUploadFailure}
                  onDropReject={onDropReject}
                  onBlur={() => form.getFieldHelpers(DELAYED_SHIPPING_BOX_IMAGE_URL).setTouched(true, true)}
                  value={form.values?.[DELAYED_SHIPPING_BOX_IMAGE_URL]}
                  onImageDelete={() => onImageDelete(DELAYED_SHIPPING_BOX_IMAGE_URL)}
                  readOnly={readOnly}
                  error={getFieldErrors(DELAYED_SHIPPING_BOX_IMAGE_URL)}
                  showErrors
                />
              )}
            </div>
            {[shouldHideCompanyNameInput, shouldHideButtonColorInput, shouldHideHeaderColorInput].some((v) => !v) && (
              <div className={styles.verticalBlock}>
                {!shouldHideCompanyNameInput && (
                  <Input
                    helperText="Company name"
                    placeholder="Company name"
                    name={DELAYED_SHIPPING_COMPANY_NAME}
                    field={form.getFieldProps(DELAYED_SHIPPING_COMPANY_NAME)}
                    value={form.values[DELAYED_SHIPPING_COMPANY_NAME]}
                    onChange={handleFieldChange}
                    disabled={isDisabled}
                    required={requiredFields[DELAYED_SHIPPING_COMPANY_NAME]}
                    error={getFieldErrors(DELAYED_SHIPPING_COMPANY_NAME)}
                    showErrors
                    readOnly={readOnly}
                  />
                )}
                {!(shouldHideButtonColorInput && shouldHideHeaderColorInput) && (
                  <div className={styles.colorInputsContainer}>
                    {!shouldHideHeaderColorInput && (
                      <ColorInput
                        isClearable
                        containerClassName={cn(styles.colorPicker)}
                        labelClassName={styles.labelWithHint}
                        helperText={
                          <React.Fragment>
                            Banner color
                            <HelpTooltip
                              id="sp-header-color-hint"
                              className={styles.infoIcon}
                              place={mobile ? PositionStylesEnum.Right : PositionStylesEnum.Top}
                              contentClassName={styles.tooltipImage}
                            >
                              <img src={spHeaderColorHintImage} alt="" />
                            </HelpTooltip>
                          </React.Fragment>
                        }
                        name={DELAYED_SHIPPING_HEADER_COLOR}
                        position={PositionStylesEnum.Right}
                        showCurrent
                        value={form.values?.[DELAYED_SHIPPING_HEADER_COLOR]}
                        onChange={(data) => handleColorChange(DELAYED_SHIPPING_HEADER_COLOR, data)}
                        readOnly={readOnly}
                      />
                    )}
                    {!shouldHideButtonColorInput && (
                      <ColorInput
                        isClearable
                        containerClassName={cn(styles.colorPicker)}
                        labelClassName={styles.labelWithHint}
                        helperText={
                          <React.Fragment>
                            Button color
                            <HelpTooltip
                              place={PositionStylesEnum.Top}
                              id="sp-button-color-hint"
                              className={styles.infoIcon}
                              contentClassName={styles.tooltipImage}
                            >
                              <img src={spButtonColorHintImage} alt="" />
                            </HelpTooltip>
                          </React.Fragment>
                        }
                        name={DELAYED_SHIPPING_BUTTON_COLOR}
                        position={PositionStylesEnum.Right}
                        showCurrent
                        value={form.values?.[DELAYED_SHIPPING_BUTTON_COLOR]}
                        onChange={(data) => handleColorChange(DELAYED_SHIPPING_BUTTON_COLOR, data)}
                        readOnly={readOnly}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className={styles.customizeInputFieldsContainer}>
            {customizationItems.length > 0 && (
              <TabProvider
                className={styles.tabProvider}
                rendererClassName={styles.customizerForm}
                tabClassName={styles.tabLabel}
                tabs={customizationItems}
                onSelect={setCustomActiveTab}
                activeTab={customActiveTab}
                loaderOnChange
              />
            )}
            <div className={styles.tags}>
              {!readOnly && availableDSTags && availableDSTags.length ? (
                <TemplateTagPicker
                  label="Available tags"
                  className={styles.tagsContainer}
                  tags={availableDSTags.map((title) => ({ title }))}
                />
              ) : null}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ShippingPageCustomizationForm;
